import { ExternalLink, Landmark } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { HeritageSite, UiLang } from "../lib/types";

interface Props {
  sites: HeritageSite[];
  lang: UiLang;
}

/**
 * Fredete bygninger i nærheten, fra Riksantikvaren.
 *
 * Samme tanke som verneområdene: appen skriver ikke om beskrivelsen, den viser
 * den og lenker videre. «Datert til om lag 1130 med utgangspunkt i
 * dendrokronologiske undersøkingar» er en presisjon ingen reiseapp har grunnlag
 * for å formulere selv — den er antikvarenes, og krediteringen står under.
 *
 * Kategorien kommer på begge språk fra API-et, ikke fra ordboka her. Det er
 * fordi den er innhold og ikke grensesnitt: en fredet kirke er en kirke uansett
 * hvilket språk knappene står på, og innholdet finnes bare på norsk og engelsk.
 */
export function HeritagePanel({ sites, lang }: Props) {
  const t = dictionaries[lang];
  if (sites.length === 0) return null;

  /*
   * Innholdsspråket er norsk eller engelsk; grensesnittet kan være fem. En tysk
   * bruker får kategorien på engelsk, som er det nærmeste vi har — se
   * hasTranslatedContent i i18n.ts for det samme skillet ellers i appen.
   */
  const contentLang = lang === "no" ? "no" : "en";

  return (
    <ul className="heritage">
      {sites.map((site) => (
        <li className="heritage-item" key={`${site.name}-${site.km}`}>
          <div className="heritage-head">
            <Landmark size={13} strokeWidth={2.2} />
            <strong className="heritage-name">{site.name}</strong>
            <span className="heritage-km">
              {site.km} {t.unitKm}
            </span>
          </div>

          <div className="heritage-meta">
            {site.kind && <span className="heritage-kind">{site.kind[contentLang]}</span>}
            {site.dated && <span className="heritage-dated">{t.heritageDated(site.dated)}</span>}
          </div>

          {site.description && <p className="heritage-text">{site.description}</p>}

          {site.link && (
            <a
              className="heritage-link"
              href={site.link}
              target="_blank"
              rel="noreferrer"
            >
              {t.heritageMore}
              <ExternalLink size={11} strokeWidth={2.4} />
            </a>
          )}
        </li>
      ))}
      <li className="heritage-source">{t.heritageSource}</li>
    </ul>
  );
}
